import { useEffect, useRef } from "react";

/**
 * FireParticles — lightweight canvas of slow rising embers.
 * Performance: ~80 particles, requestAnimationFrame, respects devicePixelRatio.
 */
export default function FireParticles({ density = 90, className = "" }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = 0;
    let h = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    const particles = [];

    const resize = () => {
      const parent = canvas.parentElement;
      w = parent.clientWidth;
      h = parent.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const rnd = (a, b) => a + Math.random() * (b - a);

    const spawn = (fromBottom = false) => ({
      x: rnd(0, w),
      y: fromBottom ? h + rnd(0, 40) : rnd(0, h),
      r: rnd(0.4, 1.8),
      vy: -rnd(0.15, 0.55),
      vx: rnd(-0.12, 0.12),
      life: 0,
      max: rnd(220, 520),
      hue: Math.random() < 0.85 ? 18 : 190, // mostly fire orange, sometimes cyan
      drift: rnd(0.002, 0.008),
    });

    const init = () => {
      particles.length = 0;
      for (let i = 0; i < density; i++) particles.push(spawn(false));
    };

    resize();
    init();

    const loop = () => {
      ctx.clearRect(0, 0, w, h);
      // slight vignette glow at base
      const g = ctx.createRadialGradient(w / 2, h + 60, 0, w / 2, h + 60, h * 0.9);
      g.addColorStop(0, "rgba(255, 77, 0, 0.12)");
      g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.life += 1;
        p.x += p.vx + Math.sin(p.life * p.drift) * 0.25;
        p.y += p.vy;
        if (p.life > p.max || p.y < -10) {
          particles[i] = spawn(true);
          continue;
        }
        const alpha = Math.max(
          0,
          Math.min(1, 1 - p.life / p.max) * 0.75,
        );
        const color =
          p.hue === 18
            ? `rgba(255, 120, 40, ${alpha})`
            : `rgba(0, 240, 255, ${alpha * 0.6})`;
        ctx.beginPath();
        ctx.fillStyle = color;
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
        // soft glow
        ctx.beginPath();
        ctx.fillStyle =
          p.hue === 18
            ? `rgba(255, 77, 0, ${alpha * 0.18})`
            : `rgba(0, 240, 255, ${alpha * 0.12})`;
        ctx.arc(p.x, p.y, p.r * 4, 0, Math.PI * 2);
        ctx.fill();
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    loop();
    const onResize = () => {
      resize();
      init();
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
    };
  }, [density]);

  return (
    <canvas
      ref={canvasRef}
      className={"absolute inset-0 pointer-events-none " + className}
      data-testid="fire-particles-canvas"
    />
  );
}
